import React from "react";
import PropTypes from "prop-types";
import Button, { ButtonPurpose } from "shared/Button";
import TextInput from "shared/@forms/TextInput";
import { Modal } from "shared/Modal";
import styles from "./SelectAddressModal.module.css";

// The id the temporary address is held under. It is deliberately not numeric so
// it can never collide with a CRM address id, and so the BFF can tell the two
// apart without a separate flag.
export const TEMP_ADDRESS_ID = "temp";

// Which address an option is, so a rep picking between two similar street
// addresses can tell why one of them is the default.
const addressTag = (address) => {
  if (address.isShipping) return "Default Shipping";
  if (address.isPrimary) return "Primary";
  return "";
};

const EMPTY_FORM = {
  street1: "",
  street2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
};

/**
 * Join the parts the rep typed into the same one-line shape CRM returns, so a
 * temporary address renders identically to one on file everywhere downstream
 * (IBO Details card, Place Order modal, the order's address snapshot).
 */
export function formatTempAddress(form) {
  const street = [form.street1, form.street2].filter(Boolean).join(", ").trim();
  const region = [form.state, form.postalCode].filter(Boolean).join(" ").trim();
  return [street, form.city, region, form.country]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");
}

// Everything except street2 is required: a partial address is worse than no
// address, because it looks deliverable and isn't.
const REQUIRED_FIELDS = ["street1", "city", "state", "postalCode", "country"];

export function isTempAddressComplete(form) {
  return REQUIRED_FIELDS.every((key) => (form[key] || "").trim().length > 0);
}

/**
 * Pick where an order ships (scope: Dylan's "Select Address" design).
 *
 * The addresses on file come from CRM and are read-only. "Add Temporary
 * Address" is a *per-order override* — it is snapshotted onto this one order
 * and is never written back to CRM, because nothing in the admin app is allowed
 * to mutate the IBO directory. A rep who needs the IBO's real address changed
 * still has to do that in CRM.
 *
 * Edits are drafted locally and only applied on Save, so Cancel genuinely
 * abandons them rather than leaving a half-made choice on the cart behind.
 */
export function SelectAddressModal({
  isOpen,
  onClose,
  onSave,
  addresses,
  shipToAddressId,
  tempAddress,
}) {
  const options = (addresses || []).filter((a) => a && a.formatted);

  const [draftId, setDraftId] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [isFormOpen, setIsFormOpen] = React.useState(false);

  // Reseed from the committed selection every time the modal opens. Keying off
  // `isOpen` rather than the props means re-fetching the IBO while the modal is
  // open can't clobber what the rep is part-way through choosing.
  React.useEffect(() => {
    if (!isOpen) return;
    setDraftId(
      tempAddress
        ? TEMP_ADDRESS_ID
        : shipToAddressId == null
        ? null
        : String(shipToAddressId)
    );
    setForm(tempAddress ? { ...EMPTY_FORM, ...tempAddress } : EMPTY_FORM);
    setIsFormOpen(Boolean(tempAddress));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setField = (key) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const formComplete = isTempAddressComplete(form);
  // Only block Save on the form when the rep is actually trying to use it; a
  // half-typed form they then abandoned by selecting an address on file is not
  // an error.
  const blockedByForm = draftId === TEMP_ADDRESS_ID && !formComplete;
  const canSave = draftId != null && !blockedByForm;

  const handleDiscardTemp = () => {
    setIsFormOpen(false);
    setForm(EMPTY_FORM);
    if (draftId !== TEMP_ADDRESS_ID) return;
    // Fall back to the selection this modal opened with, not just whichever
    // address happens to be first in the list — removing a temporary address
    // should undo it, not quietly re-route the order somewhere else.
    const committed =
      shipToAddressId == null || String(shipToAddressId) === TEMP_ADDRESS_ID
        ? null
        : String(shipToAddressId);
    const stillOnFile =
      committed != null && options.some((a) => String(a.id) === committed);
    setDraftId(stillOnFile ? committed : null);
  };

  const handleSave = () => {
    if (!canSave) return;
    if (draftId === TEMP_ADDRESS_ID) {
      const trimmed = Object.keys(EMPTY_FORM).reduce((acc, key) => {
        acc[key] = (form[key] || "").trim();
        return acc;
      }, {});
      onSave({
        shipToAddressId: TEMP_ADDRESS_ID,
        tempAddress: { ...trimmed, formatted: formatTempAddress(trimmed) },
      });
    } else {
      onSave({ shipToAddressId: draftId, tempAddress: null });
    }
    onClose();
  };

  return (
    <Modal
      title="Select Address"
      isOpen={isOpen}
      onClose={onClose}
      className={styles.modal}
    >
      <div className={styles.body}>
        {options.length ? (
          <ul className={styles.list}>
            {options.map((address) => {
              const id = String(address.id);
              const isSelected = draftId === id;
              const tag = addressTag(address);
              return (
                <li className={styles.row} key={id}>
                  <div className={styles.addressText}>
                    <span className={styles.formatted}>
                      {address.formatted}
                    </span>
                    {tag ? <span className={styles.tag}>{tag}</span> : null}
                  </div>
                  <button
                    type="button"
                    className={
                      isSelected ? styles.selectedButton : styles.selectButton
                    }
                    aria-pressed={isSelected}
                    title={
                      isSelected
                        ? "This order will ship here."
                        : "Ship this order to this address."
                    }
                    onClick={() => setDraftId(id)}
                  >
                    {isSelected ? "Selected" : "Select"}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={styles.empty}>
            No addresses came back from the IBO directory. Add a temporary
            address to give this order a destination.
          </p>
        )}

        {isFormOpen ? (
          <div className={styles.tempBlock}>
            <div className={styles.tempHeader}>
              <h4 className={styles.tempHeading}>Temporary Address</h4>
              <button
                type="button"
                className={styles.removeTemp}
                onClick={handleDiscardTemp}
              >
                Remove
              </button>
            </div>
            <p className={styles.tempNote}>
              Used for this order only. It is not saved back to the IBO&apos;s
              record in CRM.
            </p>
            <div className={styles.fields}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Street Address</span>
                <TextInput
                  value={form.street1}
                  onChange={setField("street1")}
                  placeholder="1234 Main St"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  Apt / Suite (optional)
                </span>
                <TextInput
                  value={form.street2}
                  onChange={setField("street2")}
                  placeholder="Apt 5"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>City</span>
                <TextInput value={form.city} onChange={setField("city")} />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>State</span>
                <TextInput value={form.state} onChange={setField("state")} />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>ZIP</span>
                <TextInput
                  value={form.postalCode}
                  onChange={setField("postalCode")}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Country</span>
                <TextInput
                  value={form.country}
                  onChange={setField("country")}
                />
              </label>
            </div>
            <div className={styles.tempFooter}>
              <span className={styles.preview}>
                {formComplete
                  ? formatTempAddress(form)
                  : "Fill in every field."}
              </span>
              <button
                type="button"
                className={
                  draftId === TEMP_ADDRESS_ID
                    ? styles.selectedButton
                    : styles.selectButton
                }
                aria-label="Use temporary address"
                aria-pressed={draftId === TEMP_ADDRESS_ID}
                disabled={!formComplete}
                onClick={() => setDraftId(TEMP_ADDRESS_ID)}
              >
                {draftId === TEMP_ADDRESS_ID ? "Selected" : "Select"}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.addTemp}
            onClick={() => setIsFormOpen(true)}
          >
            + Add Temporary Address
          </button>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          purpose={ButtonPurpose.generic}
          title="Close without changing where this order ships."
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          purpose={ButtonPurpose.primary}
          title={
            canSave
              ? "Ship this order to the selected address."
              : "Select an address first."
          }
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}

SelectAddressModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      formatted: PropTypes.string,
      isPrimary: PropTypes.bool,
      isShipping: PropTypes.bool,
    })
  ),
  shipToAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tempAddress: PropTypes.shape({
    street1: PropTypes.string,
    street2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    formatted: PropTypes.string,
  }),
};

SelectAddressModal.defaultProps = {
  isOpen: false,
  addresses: [],
  shipToAddressId: null,
  tempAddress: null,
};

export default SelectAddressModal;
